/**
 * The playable game: layout, render loop, drag/drop, play/stop.
 *
 * Layout follows docs/04 Part B: HUD on top, grid in the middle, parts bin
 * below it, controls at the bottom, all sized from the iPhone 13 mini floor
 * (359 pt usable width). Everything is drawn on one canvas so there is a
 * single input pipeline and a single coordinate system.
 */
import {
  decodeBoard,
  encodeBoard,
  nibbleToTile,
  peekLevelIndex,
  tileToNibble,
} from '../core/code.ts';
import { colorGlyph, colorHex, colorName, isShaded } from '../core/colors/mix.ts';
import { loadLevel, referenceSolution, type RawLevel } from '../core/levels/load.ts';
import { createSim, step } from '../core/sim/step.ts';
import { DRAGGABLE_TILES, ROTATION_CLASSES, type Rotation, type TileId } from '../core/sim/tiles.ts';
import type { Level, Placement, SimState } from '../core/sim/types.ts';
import { CHROME, drawBlob, drawTile, bevelBox } from '../render/sprites.ts';

type Mode = 'edit' | 'playing' | 'won' | 'failed';
type Screen = 'select' | 'game';

interface CtrlButton {
  id: 'menu' | 'hint' | 'reset' | 'play' | 'speed' | 'gear';
  label: string;
  color: string;
  x: number;
  w: number;
}

interface BinSlot {
  tile: TileId;
  count: number | 'inf';
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DragState {
  tile: TileId;
  rotation: Rotation;
  /** where it came from, so a cancelled drag can put it back */
  from: { kind: 'bin' } | { kind: 'grid'; x: number; y: number };
  px: number;
  py: number;
  moved: boolean;
  startX: number;
  startY: number;
}

const DRAG_THRESHOLD = 6; // pt, docs/02 5.x
const GHOST_LIFT = 40; // pt above the finger so a thumb does not hide the tile

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;

  private raws: RawLevel[];
  private levelIndex = 0;
  private level!: Level;

  /** player-placed tiles, keyed "x,y" */
  private placed = new Map<string, Placement>();
  private inventory = new Map<TileId, number | 'inf'>();

  private mode: Mode = 'edit';
  private sim: SimState | null = null;
  private lastTickAt = 0;
  private speed = 1;

  private drag: DragState | null = null;
  private bin: BinSlot[] = [];
  private toast = '';
  private toastUntil = 0;
  private tutorialCard = 0;
  private showTutorial = false;

  private screen: Screen = 'select';
  /** DOM overlay for the debug panel; created lazily, destroyed on close. */
  private debugEl: HTMLDivElement | null = null;
  /** cell highlighted by the last HINT press, cleared when it is filled */
  private hintCell: { x: number; y: number } | null = null;
  private hintUntil = 0;
  private selectRects: { i: number; x: number; y: number; w: number; h: number }[] = [];

  // layout, recomputed on resize
  private W = 0;
  private H = 0;
  private cell = 0;
  private gridX = 0;
  private gridY = 0;
  private binY = 0;
  private ctrlY = 0;

  private solvedIds: Set<string>;

  constructor(canvas: HTMLCanvasElement, raws: RawLevel[]) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas2D unavailable');
    this.ctx = ctx;
    this.raws = raws;
    this.solvedIds = new Set(this.loadProgress());

    // Resume at the first unsolved level.
    const firstUnsolved = raws.findIndex((r) => !this.solvedIds.has(r.id));
    this.levelIndex = firstUnsolved < 0 ? 0 : firstUnsolved;

    this.loadLevelAt(this.levelIndex);
    // Start on the level-select screen so the player can pick, replay a solved
    // level, or jump ahead; the game screen is one tap away.
    this.screen = 'select';
    this.bindInput();
    this.resize();
    window.addEventListener('resize', () => this.resize());
    requestAnimationFrame((t) => this.frame(t));
  }

  // ------------------------------------------------------------- persistence
  private loadProgress(): string[] {
    try {
      const raw = localStorage.getItem('pf.save.v1');
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { solved?: unknown }).solved)) {
        return (parsed as { solved: string[] }).solved;
      }
    } catch {
      /* storage can be evicted or blocked; progress is a convenience only */
    }
    return [];
  }

  private saveProgress(): void {
    try {
      localStorage.setItem('pf.save.v1', JSON.stringify({ solved: [...this.solvedIds] }));
    } catch {
      /* ignore */
    }
  }

  // ------------------------------------------------------------------ levels
  private loadLevelAt(i: number): void {
    const raw = this.raws[i];
    if (!raw) return;
    this.levelIndex = i;
    this.level = loadLevel(raw);
    this.placed.clear();
    for (const s of this.level.starters) this.placed.set(`${s.x},${s.y}`, { ...s });
    this.inventory.clear();
    for (const [tile, n] of Object.entries(this.level.inventory)) {
      this.inventory.set(tile as TileId, n);
    }
    this.mode = 'edit';
    this.sim = null;
    this.drag = null;
    this.showTutorial = (this.level.tutorial?.length ?? 0) > 0 && !this.solvedIds.has(raw.id);
    this.tutorialCard = 0;
    this.layout();
  }

  private resetBoard(): void {
    this.mode = 'edit';
    this.sim = null;
    this.loadLevelAt(this.levelIndex);
  }

  // ------------------------------------------------------------------ layout
  private resize(): void {
    this.dpr = Math.min(3, window.devicePixelRatio || 1);
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = false;
    this.W = w;
    this.H = h;
    this.layout();
  }

  private layout(): void {
    if (!this.level) return;
    const hud = 72;
    const binH = 92;
    const ctrlH = 56;
    const pad = 8;

    const availH = this.H - hud - binH - ctrlH - pad * 2;
    const availW = this.W - pad * 2;
    const { width, height } = this.level.grid;

    // Integer cell size keeps the pixel art crisp (docs/04 B).
    this.cell = Math.floor(Math.min(availW / width, availH / height));
    this.cell = Math.max(24, this.cell);

    const gridW = this.cell * width;
    const gridH = this.cell * height;
    this.gridX = Math.floor((this.W - gridW) / 2);
    this.gridY = hud + Math.floor((availH - gridH) / 2) + pad;
    this.binY = this.H - ctrlH - binH;
    this.ctrlY = this.H - ctrlH;

    // parts bin slots
    this.bin = [];
    const tiles = DRAGGABLE_TILES.filter((t) => {
      const n = this.inventory.get(t);
      return n === 'inf' || (typeof n === 'number' && n > 0);
    });
    const slotW = Math.min(76, Math.floor((this.W - 16) / Math.max(1, tiles.length)));
    const totalW = slotW * tiles.length;
    let sx = Math.floor((this.W - totalW) / 2);
    for (const t of tiles) {
      this.bin.push({
        tile: t,
        count: this.inventory.get(t) ?? 0,
        x: sx,
        y: this.binY + 10,
        w: slotW,
        h: binH - 20,
      });
      sx += slotW;
    }
  }

  // ------------------------------------------------------------------- input
  private bindInput(): void {
    const c = this.canvas;
    c.style.touchAction = 'none';
    c.addEventListener('pointerdown', (e) => this.onDown(e));
    c.addEventListener('pointermove', (e) => this.onMove(e));
    c.addEventListener('pointerup', (e) => this.onUp(e));
    c.addEventListener('pointercancel', () => {
      this.drag = null;
    });
  }

  private pt(e: PointerEvent): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private cellAt(px: number, py: number): { x: number; y: number } | null {
    const gx = Math.floor((px - this.gridX) / this.cell);
    const gy = Math.floor((py - this.gridY) / this.cell);
    if (gx < 0 || gy < 0 || gx >= this.level.grid.width || gy >= this.level.grid.height) return null;
    return { x: gx, y: gy };
  }

  private fixedAt(x: number, y: number): Placement | undefined {
    return this.level.fixed.find((p) => p.x === x && p.y === y);
  }

  private onDown(e: PointerEvent): void {
    const { x, y } = this.pt(e);
    if (this.screen === 'select') return; // handled on pointerup
    // Capture so a drag that leaves the canvas still delivers move/up events.
    // Wrapped: setPointerCapture throws for a pointer id the browser does not
    // consider active, and losing capture must never cost us the whole drag.
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch {
      /* non-fatal */
    }

    if (this.showTutorial) return; // handled on pointerup

    // controls
    if (y >= this.ctrlY) return;

    // parts bin -> start a drag
    if (y >= this.binY && this.mode === 'edit') {
      for (const slot of this.bin) {
        if (x >= slot.x && x < slot.x + slot.w && y >= slot.y && y < slot.y + slot.h) {
          const n = this.inventory.get(slot.tile);
          if (n === 'inf' || (typeof n === 'number' && n > 0)) {
            this.drag = {
              tile: slot.tile,
              rotation: 0,
              from: { kind: 'bin' },
              px: x,
              py: y,
              moved: false,
              startX: x,
              startY: y,
            };
          }
          return;
        }
      }
      return;
    }

    // grid: pick up a placed tile
    if (this.mode === 'edit') {
      const cell = this.cellAt(x, y);
      if (cell) {
        const p = this.placed.get(`${cell.x},${cell.y}`);
        if (p && !p.locked) {
          this.drag = {
            tile: p.tile,
            rotation: p.rotation,
            from: { kind: 'grid', x: cell.x, y: cell.y },
            px: x,
            py: y,
            moved: false,
            startX: x,
            startY: y,
          };
        }
      }
    }
  }

  private onMove(e: PointerEvent): void {
    if (!this.drag) return;
    const { x, y } = this.pt(e);
    this.drag.px = x;
    this.drag.py = y;
    if (
      Math.abs(x - this.drag.startX) > DRAG_THRESHOLD ||
      Math.abs(y - this.drag.startY) > DRAG_THRESHOLD
    ) {
      if (!this.drag.moved && this.drag.from.kind === 'grid') {
        // lift it off the board so the cell shows as empty while dragging
        this.placed.delete(`${this.drag.from.x},${this.drag.from.y}`);
      }
      this.drag.moved = true;
    }
  }

  private onUp(e: PointerEvent): void {
    const { x, y } = this.pt(e);

    if (this.screen === 'select') {
      this.onSelectTap(x, y);
      return;
    }

    if (this.showTutorial) {
      this.tutorialCard++;
      if (this.tutorialCard >= (this.level.tutorial?.length ?? 0)) this.showTutorial = false;
      return;
    }

    // controls row
    if (y >= this.ctrlY && !this.drag) {
      this.onControl(x);
      return;
    }

    const d = this.drag;
    this.drag = null;
    if (!d) return;

    // A tap (no movement) on a placed tile rotates it clockwise.
    if (!d.moved) {
      if (d.from.kind === 'grid') {
        const key = `${d.from.x},${d.from.y}`;
        const p = this.placed.get(key);
        if (p && !p.locked) {
          const classes = ROTATION_CLASSES[p.tile];
          if (classes.length > 1) {
            p.rotation = (((p.rotation + 90) % 360) as Rotation);
          } else {
            this.flash('NO ROTATION');
          }
        }
      }
      return;
    }

    // Dropped on the grid?
    const cell = this.cellAt(x, y - GHOST_LIFT);
    if (!cell) {
      // returned to the bin
      if (d.from.kind === 'grid') this.returnToBin(d.tile);
      return;
    }

    const occupiedFixed = this.fixedAt(cell.x, cell.y);
    const occupiedPlaced = this.placed.get(`${cell.x},${cell.y}`);
    if (occupiedFixed || occupiedPlaced) {
      // no swap/push onto an occupied cell in v1 (AGENTS.md decision 4)
      if (d.from.kind === 'grid') {
        this.placed.set(`${d.from.x},${d.from.y}`, {
          x: d.from.x,
          y: d.from.y,
          tile: d.tile,
          rotation: d.rotation,
        });
      }
      this.flash('CELL TAKEN');
      return;
    }

    this.placed.set(`${cell.x},${cell.y}`, {
      x: cell.x,
      y: cell.y,
      tile: d.tile,
      rotation: d.rotation,
    });
    if (d.from.kind === 'bin') this.takeFromBin(d.tile);
    this.layout();
  }

  private takeFromBin(t: TileId): void {
    const n = this.inventory.get(t);
    if (typeof n === 'number') this.inventory.set(t, n - 1);
  }

  private returnToBin(t: TileId): void {
    const n = this.inventory.get(t);
    if (typeof n === 'number') this.inventory.set(t, n + 1);
    this.layout();
  }

  /**
   * Control bar layout. Rendering and hit-testing both read this, so a button
   * can never be drawn somewhere it cannot be pressed.
   */
  private controlButtons(): CtrlButton[] {
    const playLabel =
      this.mode === 'playing'
        ? 'STOP'
        : this.mode === 'won'
          ? 'NEXT'
          : this.mode === 'failed'
            ? 'EDIT'
            : 'PLAY';
    const playColor =
      this.mode === 'won' ? '#11ef11' : this.mode === 'failed' ? CHROME.danger : '#1a7f2a';

    // MENU | HINT | RESET | PLAY | 1X  -- PLAY gets the widest slot because it
    // is pressed most and is the one that must never be mis-tapped.
    const weights: [CtrlButton['id'], string, string, number][] = [
      ['menu', 'MENU', CHROME.steelDark, 0.9],
      ['hint', 'HINT', '#7a5a10', 0.9],
      ['reset', 'RESET', CHROME.steel, 0.9],
      ['play', playLabel, playColor, 1.4],
      ['speed', `${this.speed}X`, CHROME.steel, 0.7],
      ['gear', '\u2699', CHROME.steelDark, 0.6],
    ];
    const total = weights.reduce((n, w) => n + w[3], 0);
    const unit = this.W / total;
    let x = 0;
    return weights.map(([id, label, color, weight]) => {
      const w = unit * weight;
      const b: CtrlButton = { id, label, color, x, w };
      x += w;
      return b;
    });
  }

  private onControl(x: number): void {
    const btn = this.controlButtons().find((b) => x >= b.x && x < b.x + b.w);
    if (!btn) return;
    switch (btn.id) {
      case 'menu':
        this.screen = 'select';
        this.mode = 'edit';
        this.sim = null;
        this.layout();
        break;
      case 'hint':
        this.giveHint();
        break;
      case 'reset':
        this.resetBoard();
        break;
      case 'play':
        if (this.mode === 'playing') {
          this.mode = 'edit';
          this.sim = null;
        } else if (this.mode === 'edit') {
          this.play();
        } else if (this.mode === 'won') {
          this.nextLevel();
        } else {
          this.mode = 'edit';
          this.sim = null;
        }
        break;
      case 'speed':
        this.speed = this.speed === 1 ? 2 : this.speed === 2 ? 4 : 1;
        this.flash(`${this.speed}X`);
        break;
      case 'gear':
        this.openDebug();
        break;
    }
  }

  // -------------------------------------------------------------- board code
  /** Nibble per cell, in reading order. */
  private boardCells(): number[] {
    const { width: w, height: h } = this.level.grid;
    const cells = new Array<number>(w * h).fill(0);
    for (const p of this.placed.values()) {
      cells[p.y * w + p.x] = tileToNibble(p.tile, p.rotation);
    }
    return cells;
  }

  private currentCode(): string {
    return encodeBoard(this.levelIndex, this.boardCells());
  }

  /** Load a pasted code. Returns an error message, or null on success. */
  private applyCode(code: string): string | null {
    let idx: number;
    try {
      idx = peekLevelIndex(code);
    } catch (err) {
      return (err as Error).message;
    }
    if (idx < 0 || idx >= this.raws.length) return `no level ${idx + 1} in this build`;

    const raw = this.raws[idx]!;
    const w = raw.grid.width;
    const h = raw.grid.height;
    let decoded;
    try {
      decoded = decodeBoard(code, w * h);
    } catch (err) {
      return (err as Error).message;
    }

    this.loadLevelAt(idx);
    this.showTutorial = false;
    this.placed.clear();

    // Rebuild the bin from the level, then spend it on the decoded tiles, so a
    // pasted board cannot conjure pieces the level does not grant.
    this.inventory.clear();
    for (const [tile, n] of Object.entries(this.level.inventory)) {
      this.inventory.set(tile as TileId, n);
    }

    let over = 0;
    decoded.cells.forEach((n, i) => {
      const state = nibbleToTile(n);
      if (!state) return;
      const x = i % w;
      const y = Math.floor(i / w);
      if (this.fixedAt(x, y)) return; // never overwrite level furniture
      const stock = this.inventory.get(state.tile);
      if (stock !== 'inf' && (typeof stock !== 'number' || stock <= 0)) {
        over++;
        return;
      }
      this.placed.set(`${x},${y}`, { x, y, ...state });
      this.takeFromBin(state.tile);
    });

    this.screen = 'game';
    this.layout();
    return over > 0 ? `loaded, but ${over} tile(s) exceeded the bin and were skipped` : null;
  }

  // ------------------------------------------------------------ debug panel
  /**
   * A DOM overlay rather than canvas-drawn: it needs real text selection, a
   * working paste target and the OS clipboard, none of which are worth
   * re-implementing on a canvas.
   */
  private openDebug(): void {
    if (this.debugEl) return;
    if (this.mode === 'playing') {
      this.mode = 'edit';
      this.sim = null;
    }

    const code = this.currentCode();
    const lvl = this.level;
    const placedCount = this.placed.size;
    const binLeft = [...this.inventory.entries()]
      .filter(([, n]) => n === 'inf' || (typeof n === 'number' && n > 0))
      .map(([t, n]) => `${t.replace('pipe_', '')}:${n}`)
      .join(' ') || 'empty';

    const el = document.createElement('div');
    el.style.cssText = [
      'position:fixed',
      'inset:0',
      'background:rgba(8,8,16,0.94)',
      'z-index:50',
      'display:flex',
      'flex-direction:column',
      'gap:10px',
      'padding:calc(env(safe-area-inset-top) + 16px) 16px 16px',
      'font:12px ui-monospace,SFMono-Regular,Menlo,monospace',
      'color:#f4f4f4',
      'overflow:auto',
      '-webkit-user-select:text',
      'user-select:text',
    ].join(';');

    el.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between">
        <strong style="color:#f8f919;letter-spacing:.1em">DEBUG</strong>
        <button id="pf-dbg-close" style="font:inherit;background:#5a6988;color:#fff;border:0;padding:8px 14px;min-height:44px">CLOSE</button>
      </div>

      <div style="color:#8fa0c0">
        LEVEL ${this.levelIndex + 1}/${this.raws.length} &middot; ${lvl.id}<br>
        grid ${lvl.grid.width}x${lvl.grid.height} &middot; mode ${lvl.mode} &middot; max_ticks ${lvl.maxTicks}<br>
        placed ${placedCount} &middot; bin ${binLeft}
      </div>

      <label style="color:#13f3ff">BOARD CODE (${code.length} chars)</label>
      <textarea id="pf-dbg-code" rows="3" spellcheck="false" autocapitalize="off" autocorrect="off"
        style="font:inherit;width:100%;background:#10101c;color:#f8f919;border:1px solid #5a6988;padding:10px;resize:vertical"
      >${code}</textarea>

      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <button id="pf-dbg-copy" style="font:inherit;flex:1;min-height:44px;background:#1a7f2a;color:#fff;border:0">COPY</button>
        <button id="pf-dbg-load" style="font:inherit;flex:1;min-height:44px;background:#7a5a10;color:#fff;border:0">LOAD</button>
      </div>
      <div id="pf-dbg-msg" style="min-height:1.4em;color:#8fa0c0"></div>

      <details style="color:#8fa0c0">
        <summary style="cursor:pointer;padding:6px 0">FORMAT</summary>
        <div style="padding-top:6px;line-height:1.6">
          <code>&lt;level base36&gt;&lt;cells base64url&gt;&lt;checksum&gt;</code><br>
          One nibble per cell in reading order; 15 tile states + empty fit a
          nibble exactly. Rotation classes collapse first, trailing empty cells
          are dropped. Paste a code and press LOAD to restore that board.
        </div>
      </details>
    `;

    const stop = (e: Event): void => e.stopPropagation();
    for (const t of ['pointerdown', 'pointermove', 'pointerup']) {
      el.addEventListener(t, stop);
    }

    const msg = (s: string, good = false): void => {
      const m = el.querySelector<HTMLDivElement>('#pf-dbg-msg');
      if (m) {
        m.textContent = s;
        m.style.color = good ? '#11ef11' : '#ff8a00';
      }
    };

    el.querySelector('#pf-dbg-close')?.addEventListener('click', () => this.closeDebug());

    el.querySelector('#pf-dbg-copy')?.addEventListener('click', () => {
      const ta = el.querySelector<HTMLTextAreaElement>('#pf-dbg-code');
      if (!ta) return;
      ta.select();
      // navigator.clipboard needs a secure context and can be blocked; the
      // legacy execCommand path is the reliable fallback inside webviews.
      void (async () => {
        try {
          await navigator.clipboard.writeText(ta.value);
          msg('copied to clipboard', true);
        } catch {
          const ok = document.execCommand?.('copy');
          msg(ok ? 'copied to clipboard' : 'press Cmd/Ctrl+C to copy', ok === true);
        }
      })();
    });

    el.querySelector('#pf-dbg-load')?.addEventListener('click', () => {
      const ta = el.querySelector<HTMLTextAreaElement>('#pf-dbg-code');
      if (!ta) return;
      const err = this.applyCode(ta.value);
      if (err === null) {
        this.closeDebug();
      } else if (err.startsWith('loaded')) {
        msg(err);
        this.closeDebug();
      } else {
        msg(err);
      }
    });

    document.body.appendChild(el);
    this.debugEl = el;
  }

  private closeDebug(): void {
    this.debugEl?.remove();
    this.debugEl = null;
  }

  /**
   * Place one correct tile from the level's reference solution.
   *
   * Deliberately *places* the piece rather than only pointing at it: these
   * levels have exact inventories, so "put a corner here" is useless if the
   * player has already spent the corner somewhere wrong. The hint therefore
   * clears any wrong tile occupying the target cell first, refunding it to the
   * bin, which is also what makes repeated presses converge on a solved board.
   */
  private giveHint(): void {
    if (this.mode === 'playing') {
      this.flash('STOP FIRST');
      return;
    }
    if (this.mode !== 'edit') {
      this.mode = 'edit';
      this.sim = null;
    }

    const raw = this.raws[this.levelIndex];
    const solution = raw ? referenceSolution(raw) : [];
    if (solution.length === 0) {
      this.flash('NO HINT FOR THIS LEVEL');
      return;
    }

    // First cell whose current contents disagree with the solution.
    const wrong = solution.find((s) => {
      const cur = this.placed.get(`${s.x},${s.y}`);
      return !cur || cur.tile !== s.tile || cur.rotation !== s.rotation;
    });

    if (!wrong) {
      // Board matches the reference; anything else on the board is surplus.
      const surplus = [...this.placed.values()].find(
        (p) => !p.locked && !solution.some((s) => s.x === p.x && s.y === p.y),
      );
      if (surplus) {
        this.placed.delete(`${surplus.x},${surplus.y}`);
        this.returnToBin(surplus.tile);
        this.hintCell = { x: surplus.x, y: surplus.y };
        this.hintUntil = performance.now() + 2000;
        this.flash('REMOVED A SPARE TILE');
      } else {
        this.flash('BOARD IS READY - PRESS PLAY');
      }
      return;
    }

    // Clear whatever is in the way, refunding it.
    const occupying = this.placed.get(`${wrong.x},${wrong.y}`);
    if (occupying) {
      if (occupying.locked) {
        this.flash('LOCKED TILE IN THE WAY');
        return;
      }
      this.placed.delete(`${wrong.x},${wrong.y}`);
      this.returnToBin(occupying.tile);
    }

    // Spend the tile from the bin if it is there; if the player has it
    // misplaced elsewhere, reclaim that copy instead of inventing one.
    const stock = this.inventory.get(wrong.tile);
    const haveStock = stock === 'inf' || (typeof stock === 'number' && stock > 0);
    if (!haveStock) {
      const misplaced = [...this.placed.values()].find(
        (p) => p.tile === wrong.tile && !p.locked && !solution.some((s) => s.x === p.x && s.y === p.y),
      );
      if (misplaced) {
        this.placed.delete(`${misplaced.x},${misplaced.y}`);
        this.returnToBin(misplaced.tile);
      } else {
        this.flash('NO TILE AVAILABLE');
        return;
      }
    }

    this.placed.set(`${wrong.x},${wrong.y}`, { ...wrong });
    this.takeFromBin(wrong.tile);
    this.hintCell = { x: wrong.x, y: wrong.y };
    this.hintUntil = performance.now() + 2500;
    this.flash('HINT PLACED');
    this.layout();
  }

  private play(): void {
    this.sim = createSim(this.level, [...this.placed.values()]);
    this.mode = 'playing';
    this.lastTickAt = performance.now();
  }

  private nextLevel(): void {
    const next = this.levelIndex + 1;
    if (next < this.raws.length) this.loadLevelAt(next);
    else this.flash('ALL LEVELS CLEAR!');
  }

  private flash(msg: string): void {
    this.toast = msg;
    this.toastUntil = performance.now() + 1200;
  }

  // -------------------------------------------------------------- game loop
  private frame(now: number): void {
    if (this.mode === 'playing' && this.sim) {
      const interval = this.level.tickMs / this.speed;
      if (now - this.lastTickAt >= interval) {
        this.lastTickAt = now;
        this.sim = step(this.sim);
        if (this.sim.status === 'won') {
          this.mode = 'won';
          this.solvedIds.add(this.level.id);
          this.saveProgress();
        } else if (this.sim.status === 'failed') {
          this.mode = 'failed';
        }
      }
    }
    this.draw();
    requestAnimationFrame((t) => this.frame(t));
  }

  // ------------------------------------------------------------------ render
  private draw(): void {
    const ctx = this.ctx;
    ctx.fillStyle = CHROME.panelDark;
    ctx.fillRect(0, 0, this.W, this.H);

    if (this.screen === 'select') {
      this.drawSelect();
      return;
    }

    this.drawHud();
    this.drawGrid();
    this.drawBlobs();
    this.drawBin();
    this.drawControls();
    this.drawGhost();
    this.drawOverlays();
  }

  private text(
    s: string,
    x: number,
    y: number,
    size: number,
    color: string,
    align: CanvasTextAlign = 'left',
  ): void {
    const ctx = this.ctx;
    ctx.fillStyle = color;
    ctx.font = `bold ${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillText(s, x, y);
  }

  private drawHud(): void {
    const ctx = this.ctx;
    bevelBox(ctx, 0, 0, this.W, 68, CHROME.panel, CHROME.steel, CHROME.panelDark);

    this.text(`LEVEL ${String(this.levelIndex + 1).padStart(2, '0')}`, 10, 16, 11, CHROME.steelLight);
    this.text(this.level.title, 10, 34, 15, CHROME.amber);

    // orders
    const sim = this.sim;
    let ox = this.W - 10;
    for (let i = this.level.fixed.length - 1; i >= 0; i--) {
      const p = this.level.fixed[i]!;
      if (p.tile !== 'bucket' || !p.order) continue;
      const got = sim?.buckets.find((b) => b.placement.x === p.x && b.placement.y === p.y)?.received ?? 0;
      const label = `${got}/${p.order.cans}`;
      this.text(label, ox, 34, 14, got === p.order.cans ? '#11ef11' : CHROME.paper, 'right');
      const w = ctx.measureText(label).width;
      // swatch
      ctx.fillStyle = colorHex(p.order.color);
      ctx.fillRect(ox - w - 24, 26, 16, 16);
      ctx.strokeStyle = CHROME.ink;
      ctx.lineWidth = 1;
      ctx.strokeRect(ox - w - 24.5, 25.5, 17, 17);
      this.text(colorGlyph(p.order.color), ox - w - 16, 34, 11, CHROME.ink, 'center');
      ox -= w + 34;
    }

    const hint = this.level.hint ?? '';
    if (hint && this.mode === 'edit') this.text(hint, 10, 55, 10, CHROME.steelLight);
    if (this.mode === 'playing') this.text(`TICK ${this.sim?.tick ?? 0}`, 10, 55, 10, '#13f3ff');
  }

  private drawGrid(): void {
    const ctx = this.ctx;
    const { width, height } = this.level.grid;

    // board backing
    ctx.fillStyle = CHROME.grid;
    ctx.fillRect(this.gridX - 4, this.gridY - 4, this.cell * width + 8, this.cell * height + 8);
    ctx.strokeStyle = CHROME.steel;
    ctx.lineWidth = 2;
    ctx.strokeRect(this.gridX - 4, this.gridY - 4, this.cell * width + 8, this.cell * height + 8);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const px = this.gridX + x * this.cell;
        const py = this.gridY + y * this.cell;
        ctx.strokeStyle = CHROME.gridLine;
        ctx.lineWidth = 1;
        ctx.strokeRect(px + 0.5, py + 0.5, this.cell - 1, this.cell - 1);

        const fixed = this.fixedAt(x, y);
        const placed = this.placed.get(`${x},${y}`);
        const p = fixed ?? placed;
        if (!p) continue;

        ctx.save();
        ctx.translate(px, py);
        const opts: Parameters<typeof drawTile>[1] = {
          tile: p.tile,
          rotation: p.rotation,
          size: this.cell,
        };
        if (p.color) opts.color = colorHex(p.color);
        if (p.locked) opts.locked = true;
        if (p.tile === 'splitter') {
          opts.next = this.sim?.splitters[`${x},${y}`] ?? 'cw';
        }
        drawTile(ctx, opts);
        ctx.restore();

        // vat can counter
        if (p.tile === 'vat') {
          const left =
            this.sim?.vats.find((v) => v.placement.x === x && v.placement.y === y)?.remaining ??
            p.cans ??
            0;
          this.text(String(left), px + this.cell / 2, py + this.cell * 0.32, this.cell * 0.3, CHROME.ink, 'center');
        }
        // bucket gauge
        if (p.tile === 'bucket' && p.order) {
          const got =
            this.sim?.buckets.find((b) => b.placement.x === x && b.placement.y === y)?.received ?? 0;
          const frac = Math.min(1, got / p.order.cans);
          const bw = this.cell * 0.5;
          const bh = this.cell * 0.5;
          const bx = px + this.cell * 0.25;
          const by = py + this.cell * 0.45;
          ctx.fillStyle = colorHex(p.order.color);
          ctx.fillRect(bx, by + bh * (1 - frac), bw, bh * frac);
          this.text(
            `${got}/${p.order.cans}`,
            px + this.cell / 2,
            py + this.cell * 0.28,
            this.cell * 0.22,
            CHROME.paper,
            'center',
          );
        }
      }
    }

    // Failure marker: a pulsing red ring on the exact cell that failed, so
    // "LEAK AT 3,3" does not require counting grid squares by hand.
    const fail = this.sim?.fail;
    if (fail && fail.x >= 0 && (this.mode === 'failed' || this.mode === 'playing')) {
      const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 100);
      ctx.strokeStyle = CHROME.danger;
      ctx.lineWidth = 2 + 3 * pulse;
      ctx.strokeRect(
        this.gridX + fail.x * this.cell + 2,
        this.gridY + fail.y * this.cell + 2,
        this.cell - 4,
        this.cell - 4,
      );
    }

    // hint highlight: a pulsing amber ring on the tile the hint just placed
    if (this.hintCell && performance.now() < this.hintUntil) {
      const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 120);
      ctx.strokeStyle = CHROME.amber;
      ctx.lineWidth = 2 + 2 * pulse;
      ctx.strokeRect(
        this.gridX + this.hintCell.x * this.cell + 2,
        this.gridY + this.hintCell.y * this.cell + 2,
        this.cell - 4,
        this.cell - 4,
      );
    }

    // drop highlight
    if (this.drag?.moved) {
      const cell = this.cellAt(this.drag.px, this.drag.py - GHOST_LIFT);
      if (cell) {
        const free = !this.fixedAt(cell.x, cell.y) && !this.placed.has(`${cell.x},${cell.y}`);
        ctx.strokeStyle = free ? CHROME.amber : CHROME.danger;
        ctx.lineWidth = 3;
        ctx.strokeRect(
          this.gridX + cell.x * this.cell + 1.5,
          this.gridY + cell.y * this.cell + 1.5,
          this.cell - 3,
          this.cell - 3,
        );
      }
    }
  }

  private drawBlobs(): void {
    if (!this.sim) return;
    for (const b of this.sim.blobs) {
      const cx = this.gridX + b.x * this.cell + this.cell / 2;
      const cy = this.gridY + b.y * this.cell + this.cell / 2;
      drawBlob(
        this.ctx,
        cx,
        cy,
        this.cell,
        colorHex(b.color),
        colorGlyph(b.color),
        b.units,
        isShaded(b.color),
      );
    }
  }

  private drawBin(): void {
    const ctx = this.ctx;
    bevelBox(ctx, 0, this.binY, this.W, this.H - this.ctrlY + 92, CHROME.panel, CHROME.steel, CHROME.panelDark);
    this.text('PARTS BIN', 10, this.binY + 12, 10, CHROME.steelLight);

    for (const slot of this.bin) {
      const size = Math.min(slot.w - 16, slot.h - 22);
      const cx = slot.x + slot.w / 2 - size / 2;
      const cy = slot.y + 14;
      ctx.save();
      ctx.translate(cx, cy);
      drawTile(ctx, { tile: slot.tile, rotation: 0, size });
      ctx.restore();
      const label = slot.count === 'inf' ? '\u221E' : String(slot.count);
      this.text(label, slot.x + slot.w / 2, cy + size + 12, 13, CHROME.amber, 'center');
    }
  }

  private drawControls(): void {
    const ctx = this.ctx;
    const y = this.ctrlY;
    const h = this.H - this.ctrlY;
    for (const b of this.controlButtons()) {
      bevelBox(ctx, b.x + 2, y + 2, b.w - 4, h - 4, b.color, CHROME.steelLight, CHROME.panelDark);
      // Clip each label to its own button. Without this a long label bleeds
      // into the neighbouring button and reads as a different word entirely
      // ("HINT" + a clipped "RESET" rendered as "CHINT").
      ctx.save();
      ctx.beginPath();
      ctx.rect(b.x + 2, y + 2, b.w - 4, h - 4);
      ctx.clip();
      let size = 14;
      ctx.font = `bold ${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      while (ctx.measureText(b.label).width > b.w - 12 && size > 8) {
        size -= 1;
        ctx.font = `bold ${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      }
      this.text(b.label, b.x + b.w / 2, y + h / 2, size, CHROME.paper, 'center');
      ctx.restore();
    }
  }

  // ----------------------------------------------------------- level select
  private drawSelect(): void {
    const ctx = this.ctx;
    ctx.fillStyle = CHROME.panelDark;
    ctx.fillRect(0, 0, this.W, this.H);

    bevelBox(ctx, 0, 0, this.W, 92, CHROME.panel, CHROME.steel, CHROME.panelDark);
    this.text('PAINT FACTORY', this.W / 2, 34, 20, CHROME.amber, 'center');
    this.text('SELECT A LEVEL', this.W / 2, 62, 11, CHROME.steelLight, 'center');

    // Fit ALL levels on one screen without scrolling: pick the column count
    // from the level count and the space available, then size cards to fit
    // the remaining height. With 24 levels a fixed 3-wide grid runs off the
    // bottom, and this canvas does not scroll.
    const pad = 8;
    const top = 100;
    const bottom = 34;
    const availH = this.H - top - bottom;
    const n = this.raws.length;
    let cols = 3;
    let cardW = 0;
    let cardH = 0;
    for (let c = 3; c <= 6; c++) {
      const w = Math.floor((this.W - pad * (c + 1)) / c);
      const rows = Math.ceil(n / c);
      const h = Math.floor((availH - pad * (rows + 1)) / rows);
      if (h >= 44 || c === 6) {
        cols = c;
        cardW = w;
        cardH = Math.min(h, Math.floor(w * 0.85));
        if (h >= 44) break;
      }
    }

    this.selectRects = [];
    this.raws.forEach((raw, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = pad + col * (cardW + pad);
      const y = top + row * (cardH + pad);
      this.selectRects.push({ i, x, y, w: cardW, h: cardH });

      const solved = this.solvedIds.has(raw.id);
      // Everything is unlocked: gating progress behind a stuck level is worse
      // than letting someone skip ahead and come back.
      bevelBox(
        ctx,
        x,
        y,
        cardW,
        cardH,
        solved ? '#1a5f2a' : CHROME.panel,
        CHROME.steelLight,
        CHROME.panelDark,
      );

      const numSize = Math.min(20, Math.floor(cardH * 0.34));
      this.text(
        String(i + 1).padStart(2, '0'),
        x + cardW / 2,
        y + cardH * 0.3,
        numSize,
        CHROME.paper,
        'center',
      );

      // order swatches tell you what the level asks for at a glance
      const orders = raw.fixed.filter((f) => f.tile === 'bucket' && f.order);
      const sw = Math.max(7, Math.min(12, Math.floor(cardH * 0.2)));
      let sx = x + cardW / 2 - (orders.length * (sw + 3) - 3) / 2;
      const sy = y + cardH * 0.55;
      for (const o of orders) {
        ctx.fillStyle = colorHex(o.order!.color);
        ctx.fillRect(sx, sy, sw, sw);
        ctx.strokeStyle = CHROME.ink;
        ctx.lineWidth = 1;
        ctx.strokeRect(sx + 0.5, sy + 0.5, sw - 1, sw - 1);
        sx += sw + 3;
      }

      const footSize = Math.max(7, Math.min(9, Math.floor(cardH * 0.16)));
      if (solved) this.text('CLEAR', x + cardW / 2, y + cardH - footSize, footSize, '#8ef08e', 'center');
      else
        this.text(
          `${raw.grid.width}x${raw.grid.height}`,
          x + cardW / 2,
          y + cardH - footSize,
          footSize,
          CHROME.steelLight,
          'center',
        );
    });

    const done = this.solvedIds.size;
    this.text(
      `${done} / ${this.raws.length} ORDERS COMPLETE`,
      this.W / 2,
      this.H - 22,
      11,
      done === this.raws.length ? '#11ef11' : CHROME.steelLight,
      'center',
    );
  }

  private onSelectTap(x: number, y: number): void {
    const hit = this.selectRects.find(
      (r) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h,
    );
    if (!hit) return;
    this.loadLevelAt(hit.i);
    this.screen = 'game';
  }

  private drawGhost(): void {
    const d = this.drag;
    if (!d || !d.moved) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.translate(d.px - this.cell / 2, d.py - GHOST_LIFT - this.cell / 2);
    drawTile(ctx, { tile: d.tile, rotation: d.rotation, size: this.cell });
    ctx.restore();
  }

  private drawOverlays(): void {
    const ctx = this.ctx;

    if (this.showTutorial && this.level.tutorial) {
      const card = this.level.tutorial[this.tutorialCard];
      if (card) {
        ctx.fillStyle = 'rgba(16,16,28,0.92)';
        ctx.fillRect(0, 0, this.W, this.H);
        const boxW = Math.min(300, this.W - 32);
        const boxH = 150;
        const bx = (this.W - boxW) / 2;
        const by = (this.H - boxH) / 2;
        bevelBox(ctx, bx, by, boxW, boxH, CHROME.panel, CHROME.steel, CHROME.panelDark);
        this.wrap(card, bx + 16, by + 42, boxW - 32, 16, 12, CHROME.paper);
        this.text(
          `TAP TO CONTINUE  (${this.tutorialCard + 1}/${this.level.tutorial.length})`,
          this.W / 2,
          by + boxH - 18,
          10,
          CHROME.amber,
          'center',
        );
      }
    }

    if (this.mode === 'won' || this.mode === 'failed') {
      const won = this.mode === 'won';
      const boxW = Math.min(280, this.W - 40);
      const boxH = 96;
      const bx = (this.W - boxW) / 2;
      const by = this.gridY + (this.level.grid.height * this.cell) / 2 - boxH / 2;
      ctx.fillStyle = 'rgba(16,16,28,0.85)';
      ctx.fillRect(bx - 6, by - 6, boxW + 12, boxH + 12);
      bevelBox(ctx, bx, by, boxW, boxH, won ? '#1a7f2a' : '#7a1010', CHROME.steelLight, CHROME.panelDark);
      // Name the ACTUAL failure. Every failure used to read "SPILL!", which
      // sent players hunting for a collision when the run had really leaked,
      // timed out or gone stuck - the message actively misled debugging.
      const reason = this.sim?.fail?.reason;
      const TITLES: Record<string, string> = {
        leak: 'LEAK!',
        spill: 'SPILL!',
        wrong_color: 'WRONG COLOUR!',
        mud: 'MUD!',
        overfill: 'OVERFILL!',
        stuck: 'STUCK!',
        timeout: 'TIMED OUT!',
      };
      const EXPLAIN: Record<string, string> = {
        leak: 'PAINT HIT A CLOSED SIDE',
        spill: 'TWO CANS MET IN ONE PIPE',
        wrong_color: 'THE BUCKET REFUSED IT',
        mud: 'THAT MIX MADE MUD',
        overfill: 'TOO MUCH PAINT',
        stuck: 'NOTHING CAN MOVE',
        timeout: 'RAN OUT OF TIME',
      };
      this.text(
        won ? 'ORDER COMPLETE!' : (TITLES[reason ?? ''] ?? 'FAILED'),
        this.W / 2,
        by + 26,
        17,
        CHROME.paper,
        'center',
      );
      this.text(
        won ? 'TAP NEXT TO CONTINUE' : (EXPLAIN[reason ?? ''] ?? ''),
        this.W / 2,
        by + 50,
        10,
        CHROME.paper,
        'center',
      );
      if (!won) {
        const f = this.sim?.fail;
        this.text(
          f && f.x >= 0 ? `AT ${f.x},${f.y} - TAP EDIT` : 'TAP EDIT',
          this.W / 2,
          by + 70,
          10,
          CHROME.amber,
          'center',
        );
      }
    }

    if (this.toast && performance.now() < this.toastUntil) {
      this.text(this.toast, this.W / 2, this.binY - 14, 12, CHROME.amber, 'center');
    }
  }

  private wrap(
    s: string,
    x: number,
    y: number,
    maxW: number,
    lh: number,
    size: number,
    color: string,
  ): void {
    const ctx = this.ctx;
    ctx.font = `bold ${size}px ui-monospace, Menlo, monospace`;
    const words = s.split(' ');
    let line = '';
    let yy = y;
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) {
        this.text(line, x, yy, size, color);
        line = w;
        yy += lh;
      } else {
        line = test;
      }
    }
    if (line) this.text(line, x, yy, size, color);
  }
}

export { colorName };
